import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { publicApi } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { ArrowLeft, Users, Clock, Check, IndianRupee } from "lucide-react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const PujaDetail = () => {
  const { id } = useParams();
  const [puja, setPuja] = useState(null);
  const [settings, setSettings] = useState({});
  const [related, setRelated] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [p, s, all] = await Promise.all([
          publicApi.pujaDetail(id),
          publicApi.settings(),
          publicApi.list("sankalp_pujas"),
        ]);
        setPuja(p);
        setSettings(s);
        setRelated(all.filter((x) => x.id !== id).slice(0, 3));
      } catch (e) {
        console.error(e);
      }
    })();
  }, [id]);

  if (!puja) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF6F0]">
        <p className="font-serif text-2xl text-[#5C544F]">Loading sacred details...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF6F0]">
      <Header brand={settings.brand_name} tagline={settings.tagline} />

      {/* Puja Banner */}
      <div className="relative w-full h-[45vh] md:h-[60vh] overflow-hidden">
        <img
          src={puja.banner_image || puja.image}
          alt={puja.name}
          className="w-full h-full object-cover kenburn"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pb-12 w-full">
            <Link
              to="/"
              data-cursor="button"
              className="inline-flex items-center gap-2 text-white/80 hover:text-[#D4AF37] text-sm tracking-wide mb-4"
            >
              <ArrowLeft size={14} /> Back to Home
            </Link>
            <div className="text-[#D4AF37] text-xs md:text-sm tracking-[0.35em] uppercase mb-3">
              Sankalp Puja
            </div>
            <h1 className="font-serif text-4xl md:text-6xl text-white tracking-tight max-w-3xl">
              {puja.name}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <h2 className="font-serif text-3xl text-[#2D2825] mb-4">About This Puja</h2>
            <p className="text-[#5C544F] leading-relaxed text-lg">
              {puja.long_desc || puja.short_desc}
            </p>

            {puja.benefits && puja.benefits.length > 0 && (
              <div className="mt-10">
                <h3 className="font-serif text-2xl text-[#2D2825] mb-5">Divine Benefits</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {puja.benefits.map((b, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 p-4 bg-white rounded-xl border border-[rgba(45,40,37,0.08)]"
                    >
                      <div className="w-6 h-6 rounded-full bg-[#8B2323]/10 flex items-center justify-center mt-0.5">
                        <Check size={13} className="text-[#8B2323]" />
                      </div>
                      <span className="text-[#2D2825]">{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {puja.gallery && puja.gallery.length > 0 && (
              <div className="mt-10">
                <h3 className="font-serif text-2xl text-[#2D2825] mb-5">Gallery</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {puja.gallery.map((g, i) => (
                    <img key={i} src={g} alt={`${puja.name} ${i}`} className="rounded-xl aspect-square object-cover" />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Card */}
          <div className="lg:sticky lg:top-24 self-start">
            <div className="bg-white rounded-2xl border border-[rgba(45,40,37,0.08)] shadow-[0_8px_30px_rgba(0,0,0,0.04)] overflow-hidden">
              <img src={puja.image} alt={puja.name} className="w-full aspect-[4/3] object-cover" />
              <div className="p-6 space-y-5">
                {puja.price > 0 && (
                  <div>
                    <div className="text-xs tracking-[0.3em] uppercase text-[#5C544F]">Dakshina</div>
                    <div className="font-serif text-4xl text-[#8B2323] flex items-center">
                      <IndianRupee size={26} className="mt-1" />
                      {Number(puja.price).toLocaleString("en-IN")}
                    </div>
                  </div>
                )}

                <div className="space-y-3 pt-2 border-t border-[rgba(45,40,37,0.08)]">
                  {puja.timing && (
                    <div className="flex items-start gap-3">
                      <Clock size={16} className="text-[#8B2323] mt-1" />
                      <div>
                        <div className="text-xs text-[#5C544F] uppercase tracking-widest">Timing</div>
                        <div className="text-[#2D2825] font-medium">{puja.timing}</div>
                      </div>
                    </div>
                  )}
                  {puja.pandit_count > 0 && (
                    <div className="flex items-start gap-3">
                      <Users size={16} className="text-[#8B2323] mt-1" />
                      <div>
                        <div className="text-xs text-[#5C544F] uppercase tracking-widest">Pandits</div>
                        <div className="text-[#2D2825] font-medium">{puja.pandit_count} Learned Pandits</div>
                      </div>
                    </div>
                  )}
                </div>

                <a
                  href={puja.inquiry_link || `https://wa.me/${String(settings.whatsapp_number || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent("Namaste, I would like to inquire about " + puja.name)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-cursor="button"
                  className="block w-full text-center bg-[#8B2323] hover:bg-[#6B1A1A] text-white py-3.5 rounded-full text-sm tracking-[0.3em] uppercase transition-colors"
                >
                  Enquire Now
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-24">
            <h3 className="font-serif text-3xl text-[#2D2825] mb-8">Other Pujas</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link
                  to={`/puja/${r.id}`}
                  key={r.id}
                  data-cursor="puja"
                  className="group bg-white rounded-2xl overflow-hidden border border-[rgba(45,40,37,0.08)] hover:shadow-[0_20px_40px_rgba(139,35,35,0.1)] transition-all"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-5">
                    <h4 className="font-serif text-xl text-[#2D2825]">{r.name}</h4>
                    <p className="text-sm text-[#5C544F] mt-1 line-clamp-2">{r.short_desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer settings={settings} />
      <WhatsAppFloat number={settings.whatsapp_number} />
    </div>
  );
};

export default PujaDetail;
